// Client-side A4 PDF export: render a fixed-size DOM node -> html2canvas raster
// -> multi-page A4 PDF via jsPDF. No server path. Ported from the reference app's
// Report Studio export logic.
//
// Keep the report DOM node's pixel size (794 x 1123 @96dpi = A4) in sync with the
// jsPDF page size below (595 x 842 pt = A4). Same aspect ratio, so the raster maps
// 1:1 onto the page width and any overflow spills onto page 2+.
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const A4_PT_WIDTH = 595;   // A4 width  in points (jsPDF "pt","a4")
const A4_PT_HEIGHT = 842;  // A4 height in points

// Resolve once every <img> under rootEl has finished loading (or errored).
// Without this, images that are still decoding capture as blank rectangles.
export async function waitForImages(rootEl) {
  if (!rootEl) return;
  const imgs = Array.from(rootEl.querySelectorAll("img"));
  await Promise.all(
    imgs.map((img) => {
      if (img.complete && img.naturalWidth > 0) return null;
      return new Promise((resolve) => {
        img.addEventListener("load", resolve, { once: true });
        img.addEventListener("error", resolve, { once: true });
      });
    })
  );
}

// Poll data-render-complete on EVERY page node until all report "true", or the timeout
// elapses. This is the shared readiness convention (also used by the Chromium render route):
// each page node sets data-render-complete="true" once its data has resolved and painted.
// Gating the first capture on this prevents snapshotting a half-drawn page.
export async function waitForRenderComplete(nodeIds, timeout = 8000) {
  const deadline = performance.now() + timeout;
  const allReady = () => nodeIds.every((id) => {
    const el = document.getElementById(id);
    return el && el.getAttribute("data-render-complete") === "true";
  });
  while (!allReady()) {
    if (performance.now() > deadline) {
      console.warn("[exportPdf] render-complete timed out; capturing current state");
      return;
    }
    await new Promise((r) => setTimeout(r, 80));
  }
}

// Rasterise an ORDERED LIST of page nodes and save them as one A4 PDF named `filename`.
// Each node is a fixed A4 tile (794×1123 ↔ 595×842pt), so it maps to exactly one full page —
// no slice/spill math. addPage() runs BETWEEN nodes, not after the last. `nodeIds` may be a
// single id (back-compat) or an ordered array. Returns true on success, false if none found.
export async function generatePdfFor(nodeIds, filename) {
  const ids = Array.isArray(nodeIds) ? nodeIds : [nodeIds];

  // Gate the entire export on readiness of ALL pages before the first capture.
  await waitForRenderComplete(ids);

  const nodes = ids.map((id) => ({ id, el: document.getElementById(id) })).filter((n) => n.el);
  if (!nodes.length) {
    console.error("[exportPdf] no report nodes found:", ids);
    return false;
  }

  const pdf = new jsPDF("p", "pt", "a4");

  for (let i = 0; i < nodes.length; i++) {
    const { el } = nodes[i];

    // Assets + fonts must be ready before the snapshot, or they render blank / fall back.
    await waitForImages(el);
    if (document.fonts && document.fonts.ready) {
      try { await document.fonts.ready; } catch (_) { /* non-fatal */ }
    }

    const canvas = await html2canvas(el, {
      scale: 2,              // 2x DPI for crisp text/lines in the raster
      useCORS: true,
      allowTaint: false,
      backgroundColor: "#ffffff",
    });
    const imgData = canvas.toDataURL("image/png");

    if (i > 0) pdf.addPage();
    // Each node is A4 aspect, so fill the full page in one placement.
    pdf.addImage(imgData, "PNG", 0, 0, A4_PT_WIDTH, A4_PT_HEIGHT);
  }

  pdf.save(filename);
  return true;
}
